import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useUpgrade } from '@/billing/use-upgrade';
import { DocLimitError, RemoteUnreachableError, type DocMeta, type DocStore, type StoredDocEnvelope } from '@/doc-sync/doc-store';
import type { SyncedDocStore } from '@/doc-sync/synced-doc-store';

import { uniqueDocName } from './doc-name';
import { readSkeletonCount, writeSkeletonCount } from './skeleton-count';

export type UseDocListOptions<TDoc extends StoredDocEnvelope, TMetaExtra> = {
    store: DocStore<TDoc, TMetaExtra> | null;
    /** Gallery-side meta filter (diagrams filter by kind); omit for all. */
    filter?: (m: DocMeta<TMetaExtra>) => boolean;
    newDoc: (name: string) => TDoc;
    hrefFor: (id: string) => string;
    skeletonKey: string;
    i18nPrefix: string;
};

export type UseDocListResult<TMetaExtra> = {
    metas: DocMeta<TMetaExtra>[] | null;
    skeletonCount: number;
    storageAvailable: boolean;
    loadError: boolean;
    offline: boolean;
    isCreating: boolean;
    createDoc: () => Promise<void>;
    renameDoc: (id: string, name: string) => Promise<void>;
    deleteDoc: (id: string) => Promise<void>;
    keepDeletedAsCopy: (id: string) => Promise<void>;
    discardDeleted: (id: string) => Promise<void>;
};

export function useDocList<TDoc extends StoredDocEnvelope, TMetaExtra>(opts: UseDocListOptions<TDoc, TMetaExtra>): UseDocListResult<TMetaExtra> {
    const { store } = opts;
    // Callers typically pass filter/newDoc/hrefFor as inline closures — fresh
    // identities every render. Read them through a ref so the callbacks below
    // can depend on [store] alone (a stable module-level singleton) — putting
    // the closures in useCallback deps would give refresh a new identity on
    // every render, and the mount effect depending on refresh would then loop
    // refresh → setMetas → re-render → refresh, hammering store.list()
    // forever. The ref preserves the original hook's fetch-once-per-mount
    // behavior without demanding memoized callers.
    const optsRef = useRef(opts);
    optsRef.current = opts;
    const { t } = useTranslation();
    const { showLimit } = useUpgrade();
    const [metas, setMetas] = useState<DocMeta<TMetaExtra>[] | null>(null);
    // Sizes the loading skeleton from the previous visit's diagram count.
    // Read once on mount — updates land on the NEXT visit, not this one.
    const [skeletonCount] = useState(() => readSkeletonCount(opts.skeletonKey));
    // Distinct from `store == null` (storage unavailable): this means the
    // store exists but a read failed, which must not read as an empty library.
    const [loadError, setLoadError] = useState(false);
    const [offline, setOffline] = useState(false);
    const isCreatingRef = useRef(false);
    const [isCreating, setIsCreating] = useState(false);

    const refresh = useCallback(async () => {
        if (store == null) {
            setMetas([]);
            setOffline(false);
            return;
        }
        try {
            const { filter, skeletonKey } = optsRef.current;
            const listed = (await store.list()).filter((m) => (filter ? filter(m) : true));
            setMetas(listed);
            writeSkeletonCount(skeletonKey, listed.length);
            setLoadError(false);
            setOffline(('lastListOffline' in store) && (store as { lastListOffline: boolean }).lastListOffline);
        } catch {
            setMetas([]);
            setLoadError(true);
            setOffline(false);
        }
    }, [store]);

    useEffect(() => {
        void refresh();
    }, [refresh]);

    // The synced store backfills thumbnails it hasn't cached AFTER list()
    // returns, so the first paint on a fresh device renders their cards blank.
    // Re-list when it signals that backfill landed — the second list() merges
    // the now-cached blobs. Feature-detected exactly like keepAsCopy below, so
    // the local-only store (which never backfills) simply never subscribes.
    useEffect(() => {
        if (store == null) return;
        const s = store as Partial<Pick<SyncedDocStore<TDoc, TMetaExtra>, 'subscribeBackgroundSync'>>;
        if (s.subscribeBackgroundSync == null) return;
        return s.subscribeBackgroundSync(() => {
            void refresh();
        });
    }, [store, refresh]);

    const createDoc = useCallback(async () => {
        if (store == null || metas == null || isCreatingRef.current) return;
        isCreatingRef.current = true;
        setIsCreating(true);
        const { newDoc, hrefFor, i18nPrefix } = optsRef.current;
        const name = uniqueDocName(t(`${i18nPrefix}.untitled`), metas.map((m) => m.name));
        const doc = newDoc(name);
        try {
            await store.save(doc);
            // Full load on purpose — mirrors the reloadDocument link pattern.
            window.location.assign(hrefFor(doc.id));
        } catch (err) {
            // With the synced store, store.save() writes locally FIRST and
            // only then syncs remote — so a RemoteUnreachableError here means
            // the doc already exists locally (it'll migrate up once back
            // online). Toasting createFailed and aborting would lie to the
            // user and a re-click would create a duplicate; proceed with
            // navigation instead and let the editor's save pill communicate
            // sync state. A DocLimitError is the server's refusal, handled
            // below. Any other failure means local.save itself failed —
            // nothing was created, so keep the toast + reset.
            if (err instanceof RemoteUnreachableError) {
                window.location.assign(hrefFor(doc.id));
                return;
            }
            isCreatingRef.current = false;
            setIsCreating(false);
            if (err instanceof DocLimitError) {
                // The server refused it for the plan's active limit (the New
                // pre-check is the first line; this is the race with another
                // device). Same local-first order as above, so the device copy
                // exists: keep it, stay here, and offer a way to make room. The
                // re-list shows it as a device-only card; it uploads once a slot
                // frees.
                showLimit();
                await refresh();
                return;
            }
            toast.error(t(`${i18nPrefix}.createFailed`));
        }
    }, [store, metas, t, showLimit, refresh]);

    const renameDoc = useCallback(async (id: string, name: string) => {
        if (store == null) return;
        try {
            const doc = await store.load(id);
            if (doc == null) return;
            await store.save({ ...doc, name, updatedAt: Date.now() });
            await refresh();
        } catch {
            toast.error(t(`${optsRef.current.i18nPrefix}.renameFailed`));
        }
    }, [store, refresh, t]);

    const deleteDoc = useCallback(async (id: string) => {
        if (store == null) return;
        // Drop the card locally first so the exit animation fires immediately on
        // confirm. store.delete + refresh() otherwise leave the card sitting there
        // until a full re-list resolves, which reads as "the delete did nothing".
        // On failure we refresh() to restore truth (the card animates back in) and
        // toast the error.
        setMetas((prev) => prev?.filter((m) => m.id !== id) ?? prev);
        try {
            await store.delete(id);
            await refresh();
        } catch {
            toast.error(t(`${optsRef.current.i18nPrefix}.deleteFailed`));
            await refresh();
        }
    }, [store, refresh, t]);

    // Only the synced store carries keepAsCopy/discardDeleted (deletedElsewhere
    // itself is only ever set by that store's list()) — the local-only store has
    // neither. Feature-detect exactly as `lastListOffline` does above rather
    // than importing a concrete store class, since this hook is shared by both
    // the diagram and pattern galleries and stays generic over TDoc/TMetaExtra.
    const keepDeletedAsCopy = useCallback(async (id: string) => {
        if (store == null) return;
        const s = store as Partial<Pick<SyncedDocStore<TDoc, TMetaExtra>, 'keepAsCopy'>>;
        if (s.keepAsCopy == null) return;
        try {
            await s.keepAsCopy(id);
            await refresh();
        } catch {
            toast.error(t(`${optsRef.current.i18nPrefix}.keepAsCopyFailed`));
        }
    }, [store, refresh, t]);

    const discardDeleted = useCallback(async (id: string) => {
        if (store == null) return;
        const s = store as Partial<Pick<SyncedDocStore<TDoc, TMetaExtra>, 'discardDeleted'>>;
        if (s.discardDeleted == null) return;
        // Optimistic removal for the same reason as deleteDoc — the discard is the
        // other confirm-dialog path that removes a card.
        setMetas((prev) => prev?.filter((m) => m.id !== id) ?? prev);
        try {
            await s.discardDeleted(id);
            await refresh();
        } catch {
            toast.error(t(`${optsRef.current.i18nPrefix}.discardFailed`));
            await refresh();
        }
    }, [store, refresh, t]);

    return {
        metas,
        skeletonCount,
        storageAvailable: store != null,
        loadError,
        offline,
        isCreating,
        createDoc,
        renameDoc,
        deleteDoc,
        keepDeletedAsCopy,
        discardDeleted,
    };
}
